var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var _vertices, _transformedVertices, _rotYMatrix, _transformMatrix;
import { Vec3D } from "./vec3d";
import { BBox3D } from "./bbox3d";
import { Matrix3D } from "./matrix3d";
export var JugglerVertex;
(function (JugglerVertex) {
    JugglerVertex[JugglerVertex["LeftHip"] = 0] = "LeftHip";
    JugglerVertex[JugglerVertex["RightHip"] = 1] = "RightHip";
    JugglerVertex[JugglerVertex["LeftShoulder"] = 2] = "LeftShoulder";
    JugglerVertex[JugglerVertex["RightShoulder"] = 3] = "RightShoulder";
    JugglerVertex[JugglerVertex["HeadCenter"] = 4] = "HeadCenter";
    JugglerVertex[JugglerVertex["HeadDX"] = 5] = "HeadDX";
    JugglerVertex[JugglerVertex["HeadDY"] = 6] = "HeadDY";
    JugglerVertex[JugglerVertex["LeftElbow"] = 7] = "LeftElbow";
    JugglerVertex[JugglerVertex["RightElbow"] = 8] = "RightElbow";
    JugglerVertex[JugglerVertex["LeftHand"] = 9] = "LeftHand";
    JugglerVertex[JugglerVertex["RightHand"] = 10] = "RightHand";
    JugglerVertex[JugglerVertex["__COUNT"] = 11] = "__COUNT";
})(JugglerVertex || (JugglerVertex = {}));
var JugglerDim;
(function (JugglerDim) {
    JugglerDim[JugglerDim["ShoulderWidth"] = 46] = "ShoulderWidth";
    JugglerDim[JugglerDim["WaistWidth"] = 34] = "WaistWidth";
    JugglerDim[JugglerDim["TorsoHeight"] = 45] = "TorsoHeight";
    JugglerDim[JugglerDim["NeckHeight"] = 5] = "NeckHeight";
    JugglerDim[JugglerDim["HeadHeight"] = 26] = "HeadHeight";
    JugglerDim[JugglerDim["HeadWidth"] = 20] = "HeadWidth";
    JugglerDim[JugglerDim["UpperArmLength"] = 41] = "UpperArmLength";
    JugglerDim[JugglerDim["LowerArmLength"] = 41] = "LowerArmLength";
})(JugglerDim || (JugglerDim = {}));
/*
 * -x = left, +x=right
 * z=0: head/body
 * z=25: hands pointing forward
 * So, +z is pointing into screen, we're looking at the juggler
 * from the back.
 */
export class Juggler {
    constructor() {
        _vertices.set(this, []);
        _transformedVertices.set(this, []);
        _rotYMatrix.set(this, new Matrix3D());
        _transformMatrix.set(this, new Matrix3D());
        for (let i = 0; i < JugglerVertex.__COUNT; i++) {
            __classPrivateFieldGet(this, _vertices).push(new Vec3D());
        }
        __classPrivateFieldGet(this, _vertices)[JugglerVertex.LeftHip].set(-JugglerDim.WaistWidth / 2, 0, 0);
        __classPrivateFieldGet(this, _vertices)[JugglerVertex.RightHip].set(JugglerDim.WaistWidth / 2, 0, 0);
        const ls = __classPrivateFieldGet(this, _vertices)[JugglerVertex.LeftShoulder];
        ls.set(-JugglerDim.ShoulderWidth / 2, JugglerDim.TorsoHeight, 0);
        const rs = __classPrivateFieldGet(this, _vertices)[JugglerVertex.RightShoulder];
        rs.set(JugglerDim.ShoulderWidth / 2, JugglerDim.TorsoHeight, 0);
        const ch = __classPrivateFieldGet(this, _vertices)[JugglerVertex.HeadCenter];
        ch.set(0, JugglerDim.TorsoHeight +
            JugglerDim.NeckHeight +
            JugglerDim.HeadHeight / 2, 0);
        __classPrivateFieldGet(this, _vertices)[JugglerVertex.HeadDX].set(JugglerDim.HeadWidth / 2, 0, 0);
        __classPrivateFieldGet(this, _vertices)[JugglerVertex.HeadDY].set(0, JugglerDim.HeadHeight / 2, 0);
        this.setHandPosition(0, new Vec3D());
        this.setHandPosition(1, new Vec3D());
        __classPrivateFieldSet(this, _transformedVertices, __classPrivateFieldGet(this, _vertices).map((v) => v.clone()));
        // https://stackoverflow.com/questions/5048701/finding-points-of-intersection-when-two-spheres-intersect
    }
    transform(translate, rotY) {
        let m = new Matrix3D();
        m = m.translate(translate);
        m = m.rotateY(rotY);
        __classPrivateFieldSet(this, _transformMatrix, m);
        let rm = new Matrix3D();
        rm = rm.rotateY(rotY);
        __classPrivateFieldSet(this, _rotYMatrix, rm);
        __classPrivateFieldGet(this, _vertices).forEach((v, i) => {
            __classPrivateFieldGet(this, _transformedVertices)[i] = (i === JugglerVertex.HeadDX ||
                i === JugglerVertex.HeadDY
                ? rm
                : m).transform(v);
        });
    }
    getTransformedVertices() {
        return __classPrivateFieldGet(this, _transformedVertices);
    }
    getPropInHandPosition(h, p, pCount) {
        const vertex = h ? JugglerVertex.RightHand : JugglerVertex.LeftHand;
        if (pCount === 1) {
            const pos = __classPrivateFieldGet(this, _transformedVertices)[vertex];
            return pos;
        }
        else {
            const upos = __classPrivateFieldGet(this, _vertices)[vertex];
            const r = new Vec3D(4, 0, 0);
            upos.iadd(r.rotY((p * Math.PI * 2) / pCount));
            return __classPrivateFieldGet(this, _transformMatrix).transform(upos);
        }
    }
    relHandPositionToAbs(handNr, dpos) {
        const shoulderVertex = handNr
            ? JugglerVertex.RightShoulder
            : JugglerVertex.LeftShoulder;
        let spos = __classPrivateFieldGet(this, _vertices)[shoulderVertex];
        let pos = new Vec3D(0, // spos.x + 0 * (handNr ? 1 : -1),
        spos.y - 35, spos.z + 25);
        pos.iadd(dpos);
        return pos;
    }
    setHandPosition(handNr, dpos) {
        const handVertex = handNr
            ? JugglerVertex.RightHand
            : JugglerVertex.LeftHand;
        const pos = this.relHandPositionToAbs(handNr, dpos);
        const elbowVertex = handNr
            ? JugglerVertex.RightElbow
            : JugglerVertex.LeftElbow;
        const shoulderVertex = handNr
            ? JugglerVertex.RightShoulder
            : JugglerVertex.LeftShoulder;
        __classPrivateFieldGet(this, _vertices)[handVertex] = pos;
        this.deriveElbowPosition(handVertex, shoulderVertex, elbowVertex);
    }
    deriveElbowPosition(handVertex, shoulderVertex, elbowVertex) {
        const elbow = __classPrivateFieldGet(this, _vertices)[elbowVertex];
        const hand = __classPrivateFieldGet(this, _vertices)[handVertex];
        const shoulder = __classPrivateFieldGet(this, _vertices)[shoulderVertex];
        const delta = hand.sub(shoulder);
        const d = delta.length();
        const u = JugglerDim.UpperArmLength;
        const l = JugglerDim.LowerArmLength;
        // https://mathworld.wolfram.com/Sphere-SphereIntersection.html
        const lr = Math.sqrt((4.0 * u * u * l * l -
            (u * u + l * l - d * d) * (u * u + l * l - d * d)) /
            (4.0 * d * d));
        if (isNaN(lr)) {
            elbow.set((shoulder.x + hand.x) / 2, (shoulder.y + hand.y) / 2, (shoulder.z + hand.z) / 2);
            return;
        }
        const f = Math.sqrt(u * u - lr * lr) / d;
        const lxsc = delta.mul(f);
        const la = Math.asin(delta.y / d);
        const f2 = 1.0 + (lr * Math.tan(la)) / (f * d);
        elbow.set(shoulder.x + lxsc.x * f2, shoulder.y + lxsc.y - lr * Math.cos(la), shoulder.z + lxsc.z * f2);
    }
    getBoundingBox() {
        let box = new BBox3D();
        for (let i = 0; i < __classPrivateFieldGet(this, _transformedVertices).length; i++) {
            switch (i) {
                case JugglerVertex.HeadDX:
                case JugglerVertex.HeadDY:
                case JugglerVertex.HeadCenter:
                    break;
                default:
                    box.add(__classPrivateFieldGet(this, _transformedVertices)[i]);
            }
        }
        const head = __classPrivateFieldGet(this, _transformedVertices)[JugglerVertex.HeadCenter];
        box.add(head.add(__classPrivateFieldGet(this, _transformedVertices)[JugglerVertex.HeadDY]));
        return box;
    }
}
_vertices = new WeakMap(), _transformedVertices = new WeakMap(), _rotYMatrix = new WeakMap(), _transformMatrix = new WeakMap();
